import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import BaseServiceApi from "BaseServiceApi";
import UserInfoUtil from "UserInfoUtil";
import AIReactionModal from "../../../../RayCustomControls/AIContent/AIReactionModal";

const EntitlementType = BaseServiceApi.rayData["EntitlementType"];


const AiInsightsContentMarkdown = ({ data, handleReaction, deltaTextSize, isMiniListOpen }) => {
  const isAIFeedbackEntitled = UserInfoUtil.IsUserEntitled(
    EntitlementType.AI_FEEDBACK_Entitlement
  );


  const getParentText = (children) => {
    let text = extractText(children);
    return text;
  };

  function splitMarkdownSections(markdown) {
    const lines = markdown.split(/\r?\n/);
    const sections = [];
    let current = null;
    lines.forEach((line) => {
      const headingMatch = line.match(/^(#{1,6})\s+(.*)$/);
      if (headingMatch) {
        if (current) sections.push(current);
        current = { heading: headingMatch[2], content: [],  headingLevel: headingMatch[1].length };
      } else if (current) {
        current.content.push(line);
      }
    });
    if (current) sections.push(current);
    return sections;
  }

  const headingFeedbackMap = {};
  const contentMap = {};
  const sections = splitMarkdownSections(data);
  for (let i = 0; i < sections.length; i++) {
    const section = sections[i];
    if (!section.heading) continue;
    let nextIndex = i + 1;
    while (
      nextIndex < sections.length &&
      sections[nextIndex].headingLevel > section.headingLevel
    ) {
      nextIndex++;
    }
    let contentLines = [...section.content];
    for (let j = i + 1; j < nextIndex; j++) {
      contentLines = contentLines.concat(sections[j].content);
    }
    const contentStr = contentLines.join("\n");
    headingFeedbackMap[section.heading] = true;
    contentMap[section.heading] = contentStr;
  }


  function extractText(children) {
    let text = "";

    React.Children.forEach(children, (child) => {
      if (typeof child === "string") {
        text += child;
      } else if (React.isValidElement(child)) {
        text += extractText(child.props.children);
      }
    });

    return text.trim();
  }


  const memonizedContent = useMemo(() => {
    const baseFontSizeMap = {
      p: 14,
      h1: 36,
      h2: 30,
      h3: 24,
      h4: 20,
      h5: 18,
      h6: 16,
      li: 14,
      ul: 14,
      ol: 14,
    };
    let lastParentText = {};
    const clamp = (val, min, max) => Math.max(min, Math.min(max, val));
    const ListItemContext = React.createContext(false);

    const getFontStyle = (tag) => ({
      fontSize: `${clamp(baseFontSizeMap[tag] + deltaTextSize, 10, 48)}px`,
    });

    return (
      <ReactMarkdown
        components={{
          p: ({ children }) => {
            const content = children?.[0];
            const style = getFontStyle("p");
            const parentHierarchy = ['h2', 'h3', 'h4', 'h5', 'h6']
              .map((key) => lastParentText[key])
              .filter((val) => !!val)
              .join('; ');
            const text = extractText(children);
            const inListItem = React.useContext(ListItemContext);

            if (typeof content === "string") {
              // Merge all children (strings and React elements) to support formatting like **bold**
              let mergedContent = '';
              children.forEach((child) => {
                if (typeof child === "string") {
                  mergedContent += child;
                } else {
                  mergedContent += extractText(child.props.children);
                }
              });

              // Check if it's a markdown-style table using merged content
              const isMarkdownTable =
                mergedContent.includes("|") && mergedContent.includes("---");

              if (isMarkdownTable) {
                const lines = mergedContent
                  .trim()
                  .split(/\r?\n/)
                  .map((line) => line.trim());
                const headerLine = lines[0] || '';
                const bodyLines = lines
                  .slice(1)
                  // drop separator rows made of dashes/colons and pipes
                  .filter((line) => !/^\s*\|?\s*[:\-\s\|]+\s*\|?\s*$/.test(line));

                const headers = headerLine
                  .split("|")
                  .map((cell) => cell.trim())
                  .filter(Boolean);
                const rows = bodyLines.map((line) =>
                  line
                    .split("|")
                    .map((cell) => cell.trim())
                    .filter(Boolean)
                );

                const tableFontSize = clamp(
                  baseFontSizeMap["p"] + deltaTextSize,
                  10,
                  48
                );

                return (
                  <table
                    border={1}
                    cellPadding={6}
                    style={{
                      fontSize: `${tableFontSize}px`,
                      borderCollapse: "collapse",
                    }}
                  >
                    <thead>
                      <tr>
                        {headers.map((header, index) => (
                          <th key={`header-${index}`}>{header}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, rowIndex) => (
                        <tr key={`row-${rowIndex}`}>
                          {row.map((cell, cellIndex) => (
                            <td key={`cell-${rowIndex}-${cellIndex}`}>
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              }

              // Fallback for regular text with \n
              const lines = content.split(/\\n|\n/);
              return (
                <div
                  style={{
                    display: isAIFeedbackEntitled ? "flex" : "",
                    gap: "5px",
                  }}
                >
                  {isAIFeedbackEntitled && !inListItem && (
                    <div style={{ marginBottom: "12px", minHeight: "25px" }}>
                      <AIReactionModal
                        text={parentHierarchy + (parentHierarchy ? "; " : "") + text}
                        handleReaction={(text, reaction, comments) =>
                          handleReaction(
                            text,
                            reaction,
                            comments,
                          )
                        }
                        isInsights
                        isMiniListOpen={isMiniListOpen}
                      />
                    </div>
                  )}
                  <p style={{ ...style, marginTop: deltaTextSize > 0 ? 3 - deltaTextSize : 3 }}>
                    {lines.map((line, index) => (
                      <React.Fragment key={index}>
                        {line}
                        {index < lines.length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </p>
                </div>
              );
            }

            return <p style={{ ...style }}>{children}</p>;
          },
          h1: ({ children }) => (
            <h1 style={getFontStyle('h1')}>{children}</h1>
          ),
          h2: ({ children }) => {
            const text = extractText(children);
            lastParentText['h2'] = getParentText(children);
            lastParentText['h3'] = "";
            lastParentText['h4'] = "";
            lastParentText['h5'] = "";
            return (
              <h2
                style={{
                  ...getFontStyle('h2'),
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {children}
                {isAIFeedbackEntitled && headingFeedbackMap[text] && (
                  <AIReactionModal
                    text={text + (text ? "; " : "") + contentMap[text]}
                    handleReaction={(text, reaction, comments) =>
                      handleReaction(
                        text,
                        reaction,
                        comments,
                      )
                    }
                    isInsights
                    stopTranslate
                    isMiniListOpen={isMiniListOpen}
                  />
                )}
              </h2>
            );
          },
          h3: ({ children }) => {
            const text = extractText(children);
            const parentHierarchy = ['h2']
              .map((key) => lastParentText[key])
              .filter((val) => !!val)
              .join('; ');
            lastParentText['h3'] = getParentText(children);
            lastParentText['h4'] = "";
            lastParentText['h5'] = "";
            return (
              <h3
                style={{
                  ...getFontStyle('h3'),
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {children}
                {isAIFeedbackEntitled && headingFeedbackMap[text] && (
                  <AIReactionModal
                    text={(parentHierarchy || "") + (parentHierarchy ? "; " : "") + (text + (text ? "; " : "") + contentMap[text])}
                    handleReaction={(text, reaction, comments) =>
                      handleReaction(
                        text,
                        reaction,
                        comments,
                      )
                    }
                    isInsights
                    stopTranslate
                    isMiniListOpen={isMiniListOpen}
                  />
                )}
              </h3>
            );
          },
          h4: ({ children }) => {
            const text = extractText(children);
            const parentHierarchy = ['h2', 'h3']
              .map((key) => lastParentText[key])
              .filter((val) => !!val)
              .join('; ');
            lastParentText['h4'] = getParentText(children);
            lastParentText['h5'] = "";
            return <h4
              style={{
                ...getFontStyle('h4'),
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {children}
              {isAIFeedbackEntitled && headingFeedbackMap[text] && (
                <AIReactionModal
                  text={(parentHierarchy || "") + (parentHierarchy ? "; " : "") + (text + (text ? "; " : "") + contentMap[text])}
                  handleReaction={(text, reaction, comments) =>
                    handleReaction(
                      text,
                      reaction,
                      comments,
                    )
                  }
                  isInsights
                  stopTranslate
                  isMiniListOpen={isMiniListOpen}
                />
              )}
            </h4>;
          },
          h5: ({ children }) => {
            const text = extractText(children);
            const parentHierarchy = ['h2', 'h3', 'h4']
              .map((key) => lastParentText[key])
              .filter((val) => !!val)
              .join('; ');
            lastParentText['h5'] = getParentText(children);
            return <h5
              style={{
                ...getFontStyle('h5'),
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {children}
              {isAIFeedbackEntitled && headingFeedbackMap[text] && (
                <AIReactionModal
                  text={(parentHierarchy || "") + (parentHierarchy ? "; " : "") + (text + (text ? "; " : "") + contentMap[text])}
                  handleReaction={(text, reaction, comments) =>
                    handleReaction(
                      text,
                      reaction,
                      comments,
                    )
                  }
                  isInsights
                  isMiniListOpen={isMiniListOpen}
                />
              )}
            </h5>;
          },
          h6: ({ children }) => (
            <h6 style={getFontStyle('h6')}>{children}</h6>
          ),
          ol: ({ children, ...props }) => (
            <ol
              {...props}
              style={{ paddingLeft: isAIFeedbackEntitled ? "15px" : "20px", ...getFontStyle('ol') }}
            >
              {children}
            </ol>
          ),
          ul: ({ children, ...props }) => (
            <ul
              {...props}
              style={{ paddingLeft: isAIFeedbackEntitled ? "15px" : "20px", ...getFontStyle('ul') }}
            >
              {children}
            </ul>
          ),
          li: ({ children, props }) => {
            const text = extractText(children);
            const parentHierarchy = ['h2', 'h3', 'h4', 'h5', 'h6']
              .map((key) => lastParentText[key])
              .filter((val) => !!val)
              .join('; ');
            return (
              <ListItemContext.Provider value={true}>
                <li
                  {...props}
                  id={`${props?.index || ''} ai-li`}
                  style={{
                    listStyleType: isAIFeedbackEntitled ? "none" : "",
                    display: isAIFeedbackEntitled ? "flex" : "",
                    gap: "5px",
                    ...getFontStyle('li'),
                  }}
                >
                  {isAIFeedbackEntitled && (
                    <div style={{ marginBottom: "12px", minHeight: "25px" }}>
                      <AIReactionModal
                        text={parentHierarchy + (parentHierarchy? "; " : "") + text}
                        handleReaction={(text, reaction, comments) =>
                          handleReaction(
                            text,
                            reaction,
                            comments,
                          )
                        }
                        isInsights
                        isMiniListOpen={isMiniListOpen}
                      />
                    </div>
                  )}
                  <p style={{ marginTop: deltaTextSize > 0 ? 2 - deltaTextSize : 2 }}>{children}</p>
                </li>
              </ListItemContext.Provider>
            );
          },
          a: ({ href, children, ...props }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              {...props}
            >
              {children}
            </a>
          ),
        }}
      >
        {data}
      </ReactMarkdown>
    );
  }, [data, deltaTextSize, isMiniListOpen]);


  return (
    <div className="space-y-4">
      {memonizedContent}
    </div>
  );
};


export default AiInsightsContentMarkdown;
